import { Plus, MoreHorizontal, Pencil, Trash2, Check, Clock } from "lucide-react";

function SelectCheckbox({ selected, onToggle }: { selected: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} className="shrink-0">
      <div className={`w-3 h-3 rounded border ${selected ? "bg-red-500 border-red-500" : "border-muted-foreground/40"} flex items-center justify-center`}>
        {selected && <Check className="w-2 h-2 text-white" />}
      </div>
    </button>
  );
}

export interface SessionListProps {
  sessions: { uuid: string; title: string }[];
  sessionUuid: string | null;
  multiSelectMode: boolean;
  selectedForDelete: Set<string>;
  toggleSelectForDelete: (uuid: string) => void;
  setSelectedForDelete: (next: Set<string>) => void;
  editingSessionId: string | null;
  editingTitle: string;
  setEditingTitle: (title: string) => void;
  setEditingSessionId: (id: string | null) => void;
  menuOpenId: string | null;
  setMenuOpenId: (id: string | null) => void;
  confirmDeleteId: string | null;
  setConfirmDeleteId: (id: string | null) => void;
  onSwitchSession: (uuid: string) => void;
  onStartRename: (uuid: string, currentTitle: string) => void;
  onCommitRename: (uuid: string) => void;
  onDeleteSession: (uuid: string) => void;
  onMultiDelete: () => void;
  onToggleMultiSelect: () => void;
  onNewSession: () => void;
}

export function SessionList({
  sessions,
  sessionUuid,
  multiSelectMode,
  selectedForDelete,
  toggleSelectForDelete,
  setSelectedForDelete,
  editingSessionId,
  editingTitle,
  setEditingTitle,
  setEditingSessionId,
  menuOpenId,
  setMenuOpenId,
  confirmDeleteId,
  setConfirmDeleteId,
  onSwitchSession,
  onStartRename,
  onCommitRename,
  onDeleteSession,
  onMultiDelete,
  onToggleMultiSelect,
  onNewSession,
}: SessionListProps) {
  const currentSession = sessions.find((s) => s.uuid === sessionUuid);

  return (
    <div className="shrink-0 border-t border-border/30">
      <div className="flex items-center px-3 py-1.5">
        <span className="text-[10px] font-medium text-muted-foreground/85 uppercase tracking-wide flex items-center gap-1 flex-1">
          <Clock className="w-3 h-3" /> Recent
        </span>
        {sessions.length > 5 && (
          <button
            onClick={onToggleMultiSelect}
            className={`text-[10px] rounded px-1.5 py-0.5 transition-colors ${multiSelectMode ? "bg-red-500/20 text-red-400" : "text-muted-foreground/80 hover:text-muted-foreground"}`}
          >
            {multiSelectMode ? "Done" : "Manage"}
          </button>
        )}
      </div>

      {/* Fixed ~5-row window; extra sessions accessible via inner scroll.
          Height matches the old slice(0, 5) footprint so the surrounding
          layout doesn't shift — only the list scrolls internally. */}
      <div className="px-1 pb-1 space-y-0.5 max-h-[148px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/60">
        {/* All sessions — blue dot on active, grey on others */}
        {sessions.map((s, idx, arr) => {
          const isActive = s.uuid === sessionUuid;
          const isLast = idx === arr.length - 1;
          return (
          <div key={s.uuid} className={`group relative flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${isActive ? "bg-muted/30" : "hover:bg-muted/40"}`}>
            {multiSelectMode ? (
              <SelectCheckbox selected={selectedForDelete.has(s.uuid)} onToggle={() => toggleSelectForDelete(s.uuid)} />
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-blue-400" : "bg-muted-foreground/20"}`} />
            )}
            {editingSessionId === s.uuid ? (
              <input
                autoFocus
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onCommitRename(s.uuid); if (e.key === "Escape") setEditingSessionId(null); }}
                onBlur={() => onCommitRename(s.uuid)}
                className="w-full text-xs rounded bg-muted border border-blue-500/50 outline-none px-1.5 py-0.5"
              />
            ) : (
              <button
                onClick={() => onSwitchSession(s.uuid)}
                onDoubleClick={() => onStartRename(s.uuid, s.title)}
                className={`text-xs truncate flex-1 text-left transition-colors ${isActive ? "font-medium text-foreground" : "text-foreground/60 hover:text-foreground/90"}`}
              >
                {s.title}
              </button>
            )}
            {!multiSelectMode && (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === s.uuid ? null : s.uuid); setConfirmDeleteId(null); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted/70 transition-opacity shrink-0"
              >
                <MoreHorizontal className="w-3 h-3 text-foreground/40" />
              </button>
            )}

            {/* Context menu */}
            {menuOpenId === s.uuid && !editingSessionId && (
              <div className={`absolute right-0 z-50 mt-0.5 w-32 rounded-md border border-border bg-popover shadow-md py-1 text-xs ${isLast ? "bottom-full mb-0.5" : "top-full"}`} data-session-menu>
                <button onClick={() => onStartRename(s.uuid, s.title)} className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-muted/50 transition-colors">
                  <Pencil className="w-3 h-3" /> Rename
                </button>
                <button onClick={() => setConfirmDeleteId(s.uuid)} className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-red-500/10 text-red-400 transition-colors">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            )}
            {confirmDeleteId === s.uuid && (
              <div className={`absolute right-0 z-50 mt-0.5 w-44 rounded-md border border-border bg-popover shadow-md p-2 text-xs ${isLast ? "bottom-full mb-0.5" : "top-full"}`} data-session-menu>
                <p className="mb-2 text-foreground/80">Delete this session?</p>
                <div className="flex gap-1 justify-end">
                  <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 rounded hover:bg-muted/50 transition-colors">Cancel</button>
                  <button onClick={() => onDeleteSession(s.uuid)} className="px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">Delete</button>
                </div>
              </div>
            )}
          </div>
          );
        })}

        {sessions.length === 0 && <div className="px-2 py-1 text-xs text-muted-foreground/80">No conversations yet</div>}

        {/* Multi-delete bar */}
        {multiSelectMode && selectedForDelete.size > 0 && (
          <div className="pt-1">
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedForDelete(selectedForDelete.size === sessions.length ? new Set() : new Set(sessions.map((s) => s.uuid)))}
                className="flex-1 px-2 py-1 text-[10px] rounded border border-border/40 text-muted-foreground hover:bg-muted/30 transition-colors"
              >
                {selectedForDelete.size === sessions.length ? "None" : "All"}
              </button>
              <button
                onClick={onMultiDelete}
                className="flex-1 px-2 py-1 text-[10px] rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors font-medium"
              >
                Delete {selectedForDelete.size}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New session button */}
      <div className="px-2 pb-2 pt-1">
        <button onClick={onNewSession} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground">
          <Plus className="w-3.5 h-3.5" /> New session
        </button>
      </div>
    </div>
  );
}
